import { ActiveThumb, UseDurationProps } from './Duration.types'
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useInstantSearch, useRange } from 'react-instantsearch'

import formatDuration from '@/utils/formatting/formatDuration'

export const STEP = 1
export const MIN_SPACE_BETWEEN = 10
export const SLIDER_LIMIT = 360 // 6 mins+

export const useSliders = (): UseDurationProps => {
	const [isRendered, setIsRendered] = useState(false)

	const { range } = useRange({
		attribute: 'Duration',
	})

	const start = useRef([-Infinity, Infinity])

	const { indexUiState, setIndexUiState } = useInstantSearch()

	const { min, max } = range

	const slidersRef = useRef()
	const minThumbRef = useRef()
	const maxThumbRef = useRef()
	const minInputRef = useRef()
	const maxInputRef = useRef()
	const minTextRef = useRef()
	const maxTextRef = useRef()
	const spaceBetweenRef = useRef()

	let activeThumb: ActiveThumb

	const getActiveThumbFromValue = (value: number): void => {
		const minInput = minInputRef.current as HTMLInputElement
		const maxInput = maxInputRef.current as HTMLInputElement

		const minValue = parseInt(minInput.value, 10)
		const maxValue = parseInt(maxInput.value, 10)

		activeThumb = value - minValue <= maxValue - value ? 'min' : 'max'
	}

	let inputTimeout: ReturnType<typeof setTimeout> | null

	const handleRefinement = (): void => {
		if (!minInputRef.current || !maxInputRef.current) {
			return
		}

		const minInput = minInputRef.current as HTMLInputElement
		const maxInput = maxInputRef.current as HTMLInputElement

		const minValue = parseInt(minInput.getAttribute('aria-valuenow'), 10)
		const maxValue = parseInt(maxInput.getAttribute('aria-valuenow'), 10)

		const reset = !minValue && maxValue === SLIDER_LIMIT

		setIndexUiState((prevState) => ({
			...prevState,
			range: {
				...prevState.range,
				Duration: reset
					? undefined
					: `${minValue}:${maxValue <= SLIDER_LIMIT ? maxValue : max}`,
			},
		}))
	}

	const updateText = useCallback((range: ActiveThumb, value: number): void => {
		if (!minTextRef.current || !minTextRef.current) {
			return
		}

		const minText = minTextRef.current as HTMLInputElement
		const maxText = maxTextRef.current as HTMLInputElement

		if (range === 'min') {
			minText.innerHTML = Number.isFinite(value)
				? formatDuration(value)
				: '0:00'
		}

		if (range === 'max') {
			maxText.innerHTML = `${
				value >= SLIDER_LIMIT ? '6:00+' : formatDuration(value)
			}`
		}
	}, [])

	const positionThumb = (range: ActiveThumb, value: number): void => {
		if (
			!minThumbRef.current ||
			!maxThumbRef.current ||
			!spaceBetweenRef.current
		) {
			return
		}

		const valueAsCssPercentage = `${Math.round((value / SLIDER_LIMIT) * 100)}%`

		const spaceBetween = spaceBetweenRef.current as HTMLElement

		if (range === 'min') {
			const thumb = minThumbRef.current as HTMLElement
			thumb.dataset.value = value.toString()
			thumb.style.left = valueAsCssPercentage
			spaceBetween.style.left = valueAsCssPercentage
		}

		if (range === 'max') {
			const thumb = maxThumbRef.current as HTMLElement

			const isAboveLimit = value >= SLIDER_LIMIT

			thumb.dataset.value = (isAboveLimit ? SLIDER_LIMIT : value).toString()
			thumb.style.left = isAboveLimit ? '100%' : valueAsCssPercentage
			spaceBetween.style.right = isAboveLimit
				? '0'
				: `${100 - Math.round((value / SLIDER_LIMIT) * 100)}%`
		}
	}

	const updateInputValues = (range: ActiveThumb, value: number): void => {
		const minInput = minInputRef.current as HTMLInputElement
		const maxInput = maxInputRef.current as HTMLInputElement

		if (range === 'min') {
			minInput.value = Number.isFinite(value) ? value.toString() : '0'
			maxInput.setAttribute(
				'min',
				Number.isFinite(value) ? (value + 1).toString() : '1'
			)
		}

		if (range === 'max') {
			maxInput.value = Number.isFinite(value)
				? value.toString()
				: SLIDER_LIMIT.toString()
			minInput.setAttribute(
				'max',
				Math.min(SLIDER_LIMIT - STEP, value - 1).toString()
			)
		}
	}

	const updateInputArias = useCallback(
		(range: ActiveThumb, value: number): void => {
			const minInput = minInputRef.current as HTMLInputElement
			const maxInput = maxInputRef.current as HTMLInputElement

			if (range === 'min') {
				minInput.setAttribute(
					'aria-valuenow',
					Number.isFinite(value) ? value.toString() : '0'
				)
				minInput.setAttribute(
					'aria-valuetext',
					formatDuration(Number.isFinite(value) ? value : 0)
				)
			}

			if (range === 'max') {
				maxInput.setAttribute('aria-valuenow', value.toString())
				maxInput.setAttribute(
					'aria-valuetext',
					`${value >= SLIDER_LIMIT ? '6:00+' : formatDuration(value)}`
				)
			}
		},
		[]
	)

	const handleMouseMove = (event: MouseEvent): void => {
		if (!slidersRef.current || !minThumbRef.current || !maxThumbRef.current) {
			return
		}

		const sliders = slidersRef.current as HTMLButtonElement

		const clientX =
			event.clientX ?? (event as unknown as TouchEvent).touches[0].clientX

		const { x, width } = sliders.getBoundingClientRect()

		let value = Math.round(((clientX - x) / width) * SLIDER_LIMIT)

		if (activeThumb === 'min' && value <= 0) {
			value = 0
		}

		if (activeThumb === 'max' && value > SLIDER_LIMIT) {
			value = SLIDER_LIMIT
		}

		if (
			activeThumb === 'min' &&
			value >
				parseInt((maxThumbRef.current as HTMLElement).dataset.value, 10) -
					MIN_SPACE_BETWEEN
		) {
			return
		}

		if (
			activeThumb === 'max' &&
			value <=
				parseInt((minThumbRef.current as HTMLElement).dataset.value, 10) +
					MIN_SPACE_BETWEEN -
					STEP
		) {
			return
		}

		positionThumb(activeThumb, value)
		updateText(activeThumb, value)
		updateInputArias(activeThumb, value)
	}

	const handleMouseUp = (event): void => {
		event.stopPropagation()
		event.preventDefault()
		document.body.style.userSelect = ''
		// Deprecated webkitUserSelect still required for Safari
		document.body.style.webkitUserSelect = ''
		document.body.style.pointerEvents = ''
		document.removeEventListener('mousemove', handleMouseMove)
		document.removeEventListener('touchmove', handleMouseMove)

		if (!minInputRef.current || !maxInputRef.current) {
			return
		}

		const valueNow = parseInt(
			activeThumb === 'min'
				? (minInputRef.current as HTMLInputElement).getAttribute(
						'aria-valuenow'
				  )
				: (maxInputRef.current as HTMLInputElement).getAttribute(
						'aria-valuenow'
				  ),
			10
		)

		updateInputValues(activeThumb, valueNow)
		handleRefinement()
	}

	const handleMouseDown = (event: React.MouseEvent): void => {
		if (!slidersRef.current) {
			return
		}

		const target = event.target as HTMLElement

		document.body.style.userSelect = 'none'
		// Deprecated webkitUserSelect still required for Safari
		document.body.style.webkitUserSelect = 'none'
		document.body.style.pointerEvents = 'none'

		const thumb = target.dataset.thumb as ActiveThumb

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp, {
			once: true,
		})

		document.addEventListener('touchmove', handleMouseMove)
		document.addEventListener('touchend', handleMouseUp, {
			once: true,
		})

		if (thumb) {
			activeThumb = thumb

			return
		}

		const clientX =
			event.clientX ?? (event as unknown as TouchEvent).touches[0].clientX

		const { x, width } = (
			slidersRef.current as HTMLButtonElement
		).getBoundingClientRect()

		const value = Math.round(((clientX - x) / width) * SLIDER_LIMIT)

		getActiveThumbFromValue(value)

		positionThumb(activeThumb, value)
		updateText(activeThumb, value)
		updateInputArias(activeThumb, value)
	}

	const handleInput = (
		event: FormEvent<HTMLInputElement>,
		range: ActiveThumb
	): void => {
		if (!minInputRef.current || !maxInputRef.current) {
			return
		}

		const value = parseInt((event.target as HTMLInputElement).value, 10)

		updateInputValues(range, value)
		updateInputArias(range, value)
		updateText(range, value)
		positionThumb(range, value)

		if (inputTimeout !== null) {
			clearTimeout(inputTimeout)
		}

		inputTimeout = setTimeout(() => {
			inputTimeout = null

			handleRefinement()
		}, 1000)
	}

	const notches = [
		{
			label: '0:00',
			value: 0,
		},
		{
			label: '1:00',
			value: 60,
		},
		{
			label: '2:00',
			value: 120,
		},
		{
			label: '3:00',
			value: 180,
		},
		{
			label: '4:00',
			value: 240,
		},
		{
			label: '5:00',
			value: 300,
		},
		{
			label: '6:00+',
			value: 360,
		},
	]

	const handleNotchClick = (value: number): void => {
		getActiveThumbFromValue(value)

		updateInputValues(activeThumb, value)
		updateInputArias(activeThumb, value)
		updateText(activeThumb, value)
		positionThumb(activeThumb, value)

		handleRefinement()
	}

	const handleLabelInput = (range: ActiveThumb): void => {
		activeThumb = range

		const input =
			range === 'min'
				? (minTextRef.current as HTMLElement)
				: (maxTextRef.current as HTMLElement)

		const { innerHTML } = input

		if (!innerHTML) {
			return
		}

		let seconds = start.current[range === 'min' ? 0 : 1]

		if (innerHTML.match(/^[0-9]+:[0-5][0-9]$/)) {
			seconds =
				parseInt(innerHTML.split(':')[0], 10) * 60 +
				parseInt(innerHTML.split(':')[1], 10)
		} else if (innerHTML.match(/[0-9]+/)) {
			seconds = parseInt(innerHTML.replace(/\D/g, ''), 10)
		} else {
			seconds = range === 'min' ? 0 : max
		}

		let value: number

		if (range === 'min') {
			value = Math.min(seconds, SLIDER_LIMIT - MIN_SPACE_BETWEEN)

			updateInputValues('min', value)
			updateInputArias('min', value)
			positionThumb('min', value)

			if (value >= start.current[1]) {
				updateInputValues('max', SLIDER_LIMIT)
				updateInputArias('max', SLIDER_LIMIT)
				updateText('max', max)
				positionThumb('max', SLIDER_LIMIT)
			}
		}

		if (range === 'max') {
			value = Math.min(seconds, SLIDER_LIMIT)

			if (value <= start.current[0]) {
				updateInputValues('max', start.current[0] + MIN_SPACE_BETWEEN)
				updateInputArias('max', start.current[0] + MIN_SPACE_BETWEEN)
				positionThumb('max', start.current[0] + MIN_SPACE_BETWEEN)
			} else {
				updateInputValues('max', value)
				updateInputArias('max', value)
				positionThumb('max', value)
			}
		}

		if (inputTimeout !== null) {
			clearTimeout(inputTimeout)
		}

		inputTimeout = setTimeout(() => {
			inputTimeout = null

			handleRefinement()
		}, 1000)
	}

	const handleLabelKeydown = (
		event: React.KeyboardEvent,
		range: ActiveThumb
	): void => {
		if (
			!minInputRef.current ||
			!maxInputRef.current ||
			!minTextRef.current ||
			!maxTextRef.current
		) {
			return
		}

		const { key } = event

		const target = event.target as HTMLElement

		if (key === 'Enter' || key === 'Escape') {
			event.preventDefault()
			event.stopPropagation()

			target.blur()

			if (range === 'min') {
				updateText(
					'min',
					parseInt((minInputRef.current as HTMLInputElement).value, 10)
				)
			}

			if (range === 'max') {
				updateText(
					'max',
					parseInt((maxInputRef.current as HTMLInputElement).value, 10)
				)
			}

			if (inputTimeout !== null) {
				clearTimeout(inputTimeout)
			}

			inputTimeout = setTimeout(() => {
				inputTimeout = null

				handleRefinement()
			}, 1000)

			return
		}

		if (key === 'ArrowUp' || key === 'ArrowDown') {
			event.preventDefault()

			const INCREMENT = key === 'ArrowUp' ? STEP : -STEP

			if (range === 'min') {
				const initialValue = parseInt(
					(minInputRef.current as HTMLInputElement).value,
					10
				)

				const value = Math.min(
					Math.max(0, initialValue + INCREMENT),
					start.current[1] - MIN_SPACE_BETWEEN
				)

				updateInputValues('min', value)
				updateInputArias('min', value)
				updateText('min', value)
				positionThumb('min', value)

				if (value > start.current[1] - MIN_SPACE_BETWEEN) {
					updateInputValues('max', SLIDER_LIMIT)
					updateInputArias('max', SLIDER_LIMIT)
					updateText('max', max)
					positionThumb('max', SLIDER_LIMIT)
				}
			}

			if (range === 'max') {
				const initialValue = parseInt(
					(maxInputRef.current as HTMLInputElement).value,
					10
				)

				const value = Math.min(
					Math.max(initialValue + INCREMENT, start.current[0]),
					SLIDER_LIMIT
				)

				if (value <= start.current[0] + MIN_SPACE_BETWEEN) {
					updateInputValues('max', start.current[0] + MIN_SPACE_BETWEEN)
					updateInputArias('max', start.current[0] + MIN_SPACE_BETWEEN)
					updateText('max', start.current[0] + MIN_SPACE_BETWEEN)
					positionThumb('max', start.current[0] + MIN_SPACE_BETWEEN)
				} else {
					updateInputValues('max', value)
					updateInputArias('max', value)
					updateText('max', value)
					positionThumb('max', value)
				}
			}

			if (inputTimeout !== null) {
				clearTimeout(inputTimeout)
			}

			inputTimeout = setTimeout(() => {
				inputTimeout = null

				handleRefinement()
			}, 1000)
		}
	}

	const handleLabelFocus = (event: React.FocusEvent): void => {
		window.getSelection().selectAllChildren(event.target as HTMLElement)
	}

	const handleLabelBlur = (range: ActiveThumb): void => {
		if (!minInputRef.current || !maxInputRef.current) {
			return
		}

		setTimeout(() => {
			if (range === 'min') {
				updateText(
					'min',
					parseInt((minInputRef.current as HTMLInputElement).value, 10)
				)
			}

			if (range === 'max') {
				updateText(
					'max',
					parseInt((maxInputRef.current as HTMLInputElement).value, 10)
				)
			}
		})
	}

	useEffect(() => {
		if (indexUiState.range?.Duration) {
			return
		}

		updateInputArias('min', 0)
		updateInputArias('max', SLIDER_LIMIT)
		updateInputValues('min', 0)
		updateInputValues('max', SLIDER_LIMIT)
		updateText('min', 0)
		updateText('max', SLIDER_LIMIT)
		positionThumb('min', 0)
		positionThumb('max', SLIDER_LIMIT)
	}, [indexUiState, updateInputArias, updateText])

	useEffect(() => {
		if (
			!minInputRef.current ||
			!maxInputRef.current ||
			!minTextRef.current ||
			!minTextRef.current ||
			!minThumbRef.current ||
			!maxThumbRef.current ||
			!spaceBetweenRef.current ||
			max === 0
		) {
			return
		}

		if (
			!Number.isFinite(start.current[0]) &&
			!Number.isFinite(start.current[1])
		) {
			const initialMin = Number.isFinite(start.current[0])
				? Math.max(start.current[0])
				: 0
			const initialMax = Number.isFinite(start.current[1])
				? Math.min(start.current[1])
				: SLIDER_LIMIT

			updateInputArias('min', initialMin)
			updateInputArias('max', initialMax)
			updateInputValues('min', initialMin)
			updateInputValues('max', initialMax)
			updateText('min', initialMin)
			updateText('max', initialMax)
			positionThumb('min', initialMin)
			positionThumb('max', initialMax)
		}
	}, [max, min, start, updateInputArias, updateText])

	/**
	 * Set inputs on page load
	 */
	useEffect(() => {
		if (
			isRendered ||
			!minInputRef.current ||
			!maxInputRef.current ||
			!minTextRef.current ||
			!minTextRef.current ||
			!minThumbRef.current ||
			!maxThumbRef.current ||
			!spaceBetweenRef.current ||
			max === 0
		) {
			return
		}

		start.current =
			indexUiState.range?.Duration?.split(':').map((value) =>
				parseInt(value, 10)
			) ?? start.current

		const initialMin = start.current[0]
		const initialMax = start.current[1]

		updateInputArias('min', initialMin)
		updateInputArias('max', initialMax)
		updateInputValues('min', initialMin)
		updateInputValues('max', initialMax)
		updateText('min', initialMin)
		updateText('max', initialMax)
		positionThumb('min', initialMin)
		positionThumb('max', initialMax)

		setIsRendered(true)
	}, [
		indexUiState.range?.Duration,
		isRendered,
		max,
		min,
		start,
		updateInputArias,
		updateText,
	])

	return {
		handleInput,
		handleMouseDown,
		handleNotchClick,
		notches,
		minInputRef,
		maxInputRef,
		minTextRef,
		maxTextRef,
		minThumbRef,
		maxThumbRef,
		slidersRef,
		SLIDER_LIMIT,
		spaceBetweenRef,
		STEP,
		handleLabelBlur,
		handleLabelInput,
		handleLabelKeydown,
		handleLabelFocus,
	}
}
